import {
  useMemo,
  useState,
} from "react";

import {
  useAARData,
} from "../../context/AARDataContext";
import { useAuth } from "../../context/AuthContext";

import "./ActionTracker.css";


function ActionTracker({
  actions = [],
  projectId = null,
  projectTitle = "",
}) {

  // =====================================================
  // AAR DATA CONTEXT
  // =====================================================

  const {
    projects,
    addAction,
    updateAction,
    deleteAction,
  } = useAARData();
  const { role } = useAuth();
  const canEditActions = role === "admin";


  // =====================================================
  // SEARCH / FILTER STATE
  // =====================================================

  const [
    searchTerm,
    setSearchTerm,
  ] = useState("");


  const [
    statusFilter,
    setStatusFilter,
  ] = useState("ALL");


  const [
    priorityFilter,
    setPriorityFilter,
  ] = useState("ALL");


  // =====================================================
  // MODAL
  // =====================================================

  const [
    showModal,
    setShowModal,
  ] = useState(false);


  const [
    editingAction,
    setEditingAction,
  ] = useState(null);


  // =====================================================
  // FORM
  // =====================================================

  const emptyForm = {
    action: "",
    owner: "",
    dueDate: "",
    priority: "MEDIUM",
    status: "OPEN",
    projectId: projectId || "",
  };


  const [
    formData,
    setFormData,
  ] = useState(emptyForm);


  // =====================================================
  // BUILD ACTION LIST
  // =====================================================

  const allActions = useMemo(() => {

    // ---------------------------------------------------
    // INDIVIDUAL AAR DETAILS PAGE
    // ---------------------------------------------------

    if (
      projectId !== null &&
      projectId !== undefined
    ) {

      return (
        Array.isArray(actions)
          ? actions
          : []
      ).map((action) => ({
        ...action,

        projectId,

        project:
          projectTitle ||
          action.project ||
          "Current AAR",
      }));

    }


    // ---------------------------------------------------
    // STANDALONE ACTION TRACKER PAGE
    // ---------------------------------------------------

    return projects.flatMap(
      (project) => {

        const projectActions =
          Array.isArray(project.actions)
            ? project.actions
            : [];


        return projectActions.map(
          (action) => ({
            ...action,

            projectId:
              project.id,

            project:
              project.title ||
              "Untitled AAR",
          })
        );

      }
    );

  }, [
    actions,
    projectId,
    projectTitle,
    projects,
  ]);


  // =====================================================
  // FILTER ACTIONS
  // =====================================================

  const filteredActions =
    useMemo(() => {

      const search =
        searchTerm
          .toLowerCase()
          .trim();


      return allActions.filter(
        (item) => {

          const action =
            String(
              item.action || ""
            ).toLowerCase();


          const owner =
            String(
              item.owner || ""
            ).toLowerCase();


          const project =
            String(
              item.project || ""
            ).toLowerCase();


          const matchesSearch =
            action.includes(search) ||
            owner.includes(search) ||
            project.includes(search);


          const matchesStatus =
            statusFilter === "ALL" ||
            item.status === statusFilter;


          const matchesPriority =
            priorityFilter === "ALL" ||
            item.priority === priorityFilter;


          return (
            matchesSearch &&
            matchesStatus &&
            matchesPriority
          );

        }
      );

    }, [
      allActions,
      searchTerm,
      statusFilter,
      priorityFilter,
    ]);


  // =====================================================
  // COUNTS
  // =====================================================

  const totalActions =
    allActions.length;


  const openActions =
    allActions.filter(
      (item) =>
        item.status === "OPEN"
    ).length;


  const inProgressActions =
    allActions.filter(
      (item) =>
        item.status === "IN PROGRESS"
    ).length;


  const completedActions =
    allActions.filter(
      (item) =>
        item.status === "COMPLETED"
    ).length;


  // =====================================================
  // OPEN ADD MODAL
  // =====================================================

  const openAddModal = () => {

    setEditingAction(null);


    setFormData({

      action: "",

      owner: "",

      dueDate: "",

      priority: "MEDIUM",

      status: "OPEN",

      projectId:
        projectId || "",

    });


    setShowModal(true);

  };


  // =====================================================
  // OPEN EDIT MODAL
  // =====================================================

  const openEditModal = (
    action
  ) => {

    setEditingAction(action);


    setFormData({

      action:
        action.action || "",

      owner:
        action.owner || "",

      dueDate:
        action.dueDate || "",

      priority:
        action.priority || "MEDIUM",

      status:
        action.status || "OPEN",

      projectId:
        action.projectId ||
        projectId ||
        "",

    });


    setShowModal(true);

  };


  // =====================================================
  // CLOSE MODAL
  // =====================================================

  const closeModal = () => {

    setShowModal(false);

    setEditingAction(null);

    setFormData({
      ...emptyForm,
      projectId:
        projectId || "",
    });

  };


  // =====================================================
  // FORM CHANGE
  // =====================================================

  const handleChange = (
    event
  ) => {

    const {
      name,
      value,
    } = event.target;


    setFormData(
      (previous) => ({
        ...previous,
        [name]: value,
      })
    );

  };


  // =====================================================
  // SAVE ACTION
  // =====================================================

  const handleSubmit = (
    event
  ) => {

    event.preventDefault();


    // ---------------------------------------------------
    // VALIDATION
    // ---------------------------------------------------

    if (
      !formData.action.trim()
    ) {

      alert(
        "Please enter an action."
      );

      return;
    }


    if (
      !formData.owner.trim()
    ) {

      alert(
        "Please enter an owner."
      );

      return;
    }


    const selectedProjectId =
      projectId ||
      Number(formData.projectId);


    if (
      !selectedProjectId
    ) {

      alert(
        "Please select an AAR project."
      );

      return;
    }


    // ===================================================
    // UPDATE EXISTING ACTION
    // ===================================================

    if (editingAction) {

      updateAction(
        Number(
          editingAction.projectId ||
          selectedProjectId
        ),

        editingAction.id,

        {
          action:
            formData.action.trim(),

          owner:
            formData.owner.trim(),

          dueDate:
            formData.dueDate,

          priority:
            formData.priority,

          status:
            formData.status,
        }
      );


      closeModal();

      return;
    }


    // ===================================================
    // CREATE NEW ACTION
    // ===================================================

    addAction(
      Number(selectedProjectId),

      {
        action:
          formData.action.trim(),

        owner:
          formData.owner.trim(),

        dueDate:
          formData.dueDate ||
          "No Due Date",

        priority:
          formData.priority,

        status:
          formData.status,
      }
    );


    closeModal();

  };


  // =====================================================
  // UPDATE STATUS
  // =====================================================

  const handleStatusChange = (
    action,
    newStatus
  ) => {

    if (!canEditActions) {
      return;
    }

    const currentProjectId =
      Number(
        action.projectId ||
        projectId
      );


    if (!currentProjectId) {
      return;
    }


    updateAction(
      currentProjectId,

      action.id,

      {
        status:
          newStatus,
      }
    );

  };


  // =====================================================
  // DELETE ACTION
  // =====================================================

  const handleDelete = (
    action
  ) => {

    if (!canEditActions) {
      return;
    }

    const confirmed =
      window.confirm(
        "Are you sure you want to delete this action?"
      );


    if (!confirmed) {
      return;
    }


    const currentProjectId =
      Number(
        action.projectId ||
        projectId
      );


    if (!currentProjectId) {
      return;
    }


    deleteAction(
      currentProjectId,
      action.id
    );

  };


  // =====================================================
  // RESET FILTERS
  // =====================================================

  const resetFilters = () => {

    setSearchTerm("");

    setStatusFilter("ALL");

    setPriorityFilter("ALL");

  };


  // =====================================================
  // RENDER
  // =====================================================

  return (

    <div className="action-tracker-page">


      {/* =================================================
          HEADER
      ================================================= */}

      <section className="action-page-header">

        <div className="action-header-container">

          <span className="action-eyebrow">
            AFTER ACTION REVIEW
          </span>

          <h1>
            Action Tracker
          </h1>

          <p>
            Track improvement actions resulting from
            After Action Reviews.
          </p>

        </div>

      </section>


      {/* =================================================
          MAIN
      ================================================= */}

      <main className="action-container">


        {/* =================================================
            STATS
        ================================================= */}

        <section className="action-stats">


          <div className="action-stat-card">

            <span>
              TOTAL ACTIONS
            </span>

            <strong>
              {totalActions}
            </strong>

            <small>
              All improvement actions
            </small>

          </div>


          <div className="action-stat-card">

            <span>
              OPEN
            </span>

            <strong>
              {openActions}
            </strong>

            <small>
              Require attention
            </small>

          </div>


          <div className="action-stat-card">

            <span>
              IN PROGRESS
            </span>

            <strong>
              {inProgressActions}
            </strong>

            <small>
              Currently being worked on
            </small>

          </div>


          <div className="action-stat-card">

            <span>
              COMPLETED
            </span>

            <strong>
              {completedActions}
            </strong>

            <small>
              Successfully completed
            </small>

          </div>


        </section>


        {/* =================================================
            ACTION MANAGEMENT
        ================================================= */}

        <section className="action-management">


          <div className="action-management-header">

            <div>

              <span>
                IMPROVEMENT MANAGEMENT
              </span>

              <h2>
                Improvement Actions
              </h2>

              <p>
                Track actions resulting from After Action Reviews.
              </p>

            </div>


            {canEditActions && (
              <button
                type="button"
                className="add-action-button"
                onClick={openAddModal}
              >
                + Add Action
              </button>
            )}

          </div>


          {/* =================================================
              FILTERS
          ================================================= */}

          <div className="action-filters">

            <input
              type="text"
              placeholder="Search actions, owners or projects..."
              value={searchTerm}
              onChange={(event) =>
                setSearchTerm(
                  event.target.value
                )
              }
            />


            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >

              <option value="ALL">
                All Statuses
              </option>

              <option value="OPEN">
                Open
              </option>

              <option value="IN PROGRESS">
                In Progress
              </option>

              <option value="COMPLETED">
                Completed
              </option>

            </select>


            <select
              value={priorityFilter}
              onChange={(event) =>
                setPriorityFilter(
                  event.target.value
                )
              }
            >

              <option value="ALL">
                All Priorities
              </option>

              <option value="HIGH">
                High
              </option>

              <option value="MEDIUM">
                Medium
              </option>

              <option value="LOW">
                Low
              </option>

            </select>

          </div>


          {/* =================================================
              TABLE
          ================================================= */}

          {filteredActions.length > 0 ? (

            <div className="action-table-wrapper">

              <table className="action-table">

                <thead>

                  <tr>

                    <th>
                      ACTION
                    </th>

                    <th>
                      OWNER
                    </th>

                    <th>
                      DUE DATE
                    </th>

                    <th>
                      PRIORITY
                    </th>

                    <th>
                      STATUS
                    </th>

                    <th>
                      PROJECT
                    </th>

                    {canEditActions && (
                      <th>
                        ACTIONS
                      </th>
                    )}

                  </tr>

                </thead>


                <tbody>

                  {filteredActions.map(
                    (item) => (

                      <tr
                        key={`${item.projectId}-${item.id}`}
                      >


                        <td>

                          <strong>
                            {item.action}
                          </strong>

                        </td>


                        <td>
                          {item.owner}
                        </td>


                        <td>
                          {item.dueDate}
                        </td>


                        <td>

                          <span
                            className={`priority-badge ${String(
                              item.priority ||
                              "MEDIUM"
                            ).toLowerCase()}`}
                          >
                            {item.priority ||
                              "MEDIUM"}
                          </span>

                        </td>


                        <td>

                          <select
                            value={
                              item.status ||
                              "OPEN"
                            }
                            disabled={!canEditActions}
                            onChange={(
                              event
                            ) =>
                              handleStatusChange(
                                item,
                                event.target.value
                              )
                            }
                            className={`status-select ${String(
                              item.status ||
                              "OPEN"
                            )
                              .toLowerCase()
                              .replace(
                                /\s+/g,
                                "-"
                              )}`}
                          >

                            <option value="OPEN">
                              OPEN
                            </option>

                            <option value="IN PROGRESS">
                              IN PROGRESS
                            </option>

                            <option value="COMPLETED">
                              COMPLETED
                            </option>

                          </select>

                        </td>


                        <td>
                          {item.project}
                        </td>


                        {canEditActions && (
                          <td>

                            <div className="action-buttons">

                            <button
                              type="button"
                              className="edit-action-button"
                              onClick={() =>
                                openEditModal(
                                  item
                                )
                              }
                              title="Edit action"
                            >
                              ✏️
                            </button>


                            <button
                              type="button"
                              className="delete-action-button"
                              onClick={() =>
                                handleDelete(
                                  item
                                )
                              }
                              title="Delete action"
                            >
                              🗑️
                            </button>

                            </div>

                          </td>
                        )}


                      </tr>

                    )
                  )}

                </tbody>

              </table>

            </div>

          ) : (

            <div className="no-actions">

              <div className="no-actions-icon">
                🔍
              </div>

              <h3>
                No actions found
              </h3>

              <p>
                Try changing your search or filters.
              </p>

              {(searchTerm ||
                statusFilter !== "ALL" ||
                priorityFilter !== "ALL") && (

                <button
                  type="button"
                  onClick={resetFilters}
                >
                  Clear Filters
                </button>

              )}

            </div>

          )}


        </section>

      </main>


      {/* =================================================
          ADD / EDIT MODAL
      ================================================= */}

      {showModal && (

        <div
          className="action-modal-overlay"
          onClick={closeModal}
        >

          <div
            className="action-modal"
            onClick={(event) =>
              event.stopPropagation()
            }
          >


            {/* =================================================
                MODAL HEADER
            ================================================= */}

            <div className="action-modal-header">

              <div>

                <span>
                  {editingAction
                    ? "UPDATE IMPROVEMENT ACTION"
                    : "NEW IMPROVEMENT ACTION"}
                </span>

                <h2>
                  {editingAction
                    ? "Edit Action"
                    : "Add Action"}
                </h2>

              </div>


              <button
                type="button"
                className="modal-close"
                onClick={closeModal}
              >
                ×
              </button>

            </div>


            {/* =================================================
                FORM
            ================================================= */}

            <form
              onSubmit={handleSubmit}
            >


              {/* ACTION */}

              <div className="form-group">

                <label>
                  ACTION *
                </label>

                <textarea
                  name="action"
                  value={formData.action}
                  onChange={handleChange}
                  placeholder="Enter improvement action..."
                  rows="3"
                  required
                />

              </div>


              {/* OWNER */}

              <div className="form-group">

                <label>
                  OWNER *
                </label>

                <input
                  type="text"
                  name="owner"
                  value={formData.owner}
                  onChange={handleChange}
                  placeholder="e.g. Project Manager"
                  required
                />

              </div>


              {/* PROJECT */}

              {!projectId && (

                <div className="form-group">

                  <label>
                    PROJECT *
                  </label>

                  <select
                    name="projectId"
                    value={
                      formData.projectId
                    }
                    onChange={
                      handleChange
                    }
                    required
                  >

                    <option value="">
                      Select AAR Project
                    </option>


                    {projects.map(
                      (project) => (

                        <option
                          key={project.id}
                          value={project.id}
                        >
                          {project.title}
                        </option>

                      )
                    )}

                  </select>

                </div>

              )}


              {/* CURRENT AAR */}

              {projectId && (

                <div className="form-group">

                  <label>
                    PROJECT
                  </label>

                  <input
                    type="text"
                    value={
                      projectTitle ||
                      "Current AAR"
                    }
                    readOnly
                  />

                </div>

              )}


              {/* DATE / PRIORITY / STATUS */}

              <div className="form-row three-columns">


                <div className="form-group">

                  <label>
                    DUE DATE
                  </label>

                  <input
                    type="date"
                    name="dueDate"
                    value={
                      formData.dueDate
                    }
                    onChange={
                      handleChange
                    }
                  />

                </div>


                <div className="form-group">

                  <label>
                    PRIORITY
                  </label>

                  <select
                    name="priority"
                    value={
                      formData.priority
                    }
                    onChange={
                      handleChange
                    }
                  >

                    <option value="HIGH">
                      High
                    </option>

                    <option value="MEDIUM">
                      Medium
                    </option>

                    <option value="LOW">
                      Low
                    </option>

                  </select>

                </div>


                <div className="form-group">

                  <label>
                    STATUS
                  </label>

                  <select
                    name="status"
                    value={
                      formData.status
                    }
                    onChange={
                      handleChange
                    }
                  >

                    <option value="OPEN">
                      Open
                    </option>

                    <option value="IN PROGRESS">
                      In Progress
                    </option>

                    <option value="COMPLETED">
                      Completed
                    </option>

                  </select>

                </div>


              </div>


              {/* BUTTONS */}

              <div className="action-modal-actions">

                <button
                  type="button"
                  className="modal-cancel-button"
                  onClick={closeModal}
                >
                  Cancel
                </button>


                <button
                  type="submit"
                  className="modal-save-button"
                >
                  {editingAction
                    ? "Save Changes"
                    : "Add Action"}
                </button>

              </div>


            </form>

          </div>

        </div>

      )}

    </div>

  );

}


export default ActionTracker;